import Config from '@kit/utils/Config'
import { ref, toRef, watch } from 'vue'
import { endChar } from '@kit/utils/Formats'
import { nextTick } from 'vue'
import { getRandomID } from '@kit/utils/aria/Aria'

/*
  Themer utility.

  
  export default defineComponent({
    name: "MyModal",
    
    --The props can have an optional theme and optional theme properties.
    There's a chain or precedence a given property will look first to the props.
    Then it will look to the theme that you've passed in, and finally it will look to the
    default-configuration.

    props:{
      //the theme. Optional. 
      "theme":{ required: false }

      //exposed theme properties
      "themeProp1": { required: false }
      "themeProp2": { required: false } 
    },

    -- Get the all-important getProp and setProp functions from the themer injection.
    You can use setProp to reskin parts of the theme.
    setup(props, context) {

      const themer = inject("themer")
      const { getProp, setProp, reskin } = themer({ props, defaultConfig })
    
      //...do other stuff here

      //expose these so that the owner of this component can to reskinning.
      context.expose({ reskin, setProp })

      return { getProp, ...otherstuff }
    }

  -- In the template, getProp is used to fetch the properties:
  
  <some-component color="getProp('themeProp1.color')"/>

  NOTE. If you want to 'force' a change using setProp, pass the true in the force parameter.
  setProp("imageCoverCSS", `background-color:rgba(0,0,255,0.4)`, false, true)

  NOTE. What I'd really like is the ability to reskin themes as well as individual components.
  
  _theTheme.reskin({...})
  myModal.value.reskin({...})

  Also, the props aren't reactive. 
  So if you do this with the IconButton:


  <IconButton :theme="theTheme" :text="someTextComputedProperty">

  Well... see... the "text" stops being reactive. So once you go with the theme route
  you really have to do the reactivity through the theme reskinning.


  Background:

  Here's the deal. Flexible components with many different kinds of modes and 
  behaviors are hard. You end up with like 100 properties to specify in the markup.
  Well, that's just kind of nuts.

  It will occur to you to just wrap everything into some kind of theme object,
  that way you can just pass in a single object which will have all those options 
  baked in, and you can the maintain a set of files with theme objects. ES6 makes 
  this easier to do with the spread operator. Not a bad plan. That's how this started.

  The plan gets tricker when you have to reskin the theme or parts of it. What to do?
  Could do something where the entire theme is reactive. Problem there is performance.
  Deep reactivity on these theme objects isn't practical. 

  Anther idea is shallow-refs. Make the theme sort-of reactive at least on a shallow 
  level with the shallowRefs function. I've tried that and it actually works ok. But
  not ok enough. You can tell yourself that your theme will always be shallow, but 
  it just won't.

  Ok, but there's another prerogative here, which is a chain-of-precedence between the 
  theme object, whatever props overrides you might have on your component, and a 
  default theme object. For some property "x": props.x || theme.x || defaultTheme.x
  
  Now, the trick is, making this kind of chain-of-precedence work with reactivity.
  Well, that's why we have this wrapper class ThemeWrapper, and each instance contains
  a reactive ref() object. Then the component gets a getProp and setProp function. getProp
  is reactive because it consumes the ref. setProp updates the config object and the ref.

  See right at the bottom: the themer function. That it wired into the app so you can 
  access it in your components with inject("themer")

  Couple of bugaboos:
  1. We can't do "deep" reskinning.
  See, I have to do this:

        navBarBase.value.reskin([
          { key:"logoSrc", value:pathname("@images/ics-logo-FINAL.png") },
          { key:"hamburgerButtonClasses", value:"ics-nav-hamburger-nav-button" },
          { key:"outerContainerClassesDesktop", value:"ics-navbar-boxshadow" },
          { key:"outerContainerClassesMobile", value:"ics-navbar-boxshadow" },
        ])
 
        //Where we have a special exposed function to reskin an interior object
        //which consumes a button sub-theme.
        navMainSearch.value.reskinSearchButton([
          { key:'foreground', value:"#555555" },
          { key:'iconDimension', value:{
              style: ButtonIconActiveHeight,
              active: '20px'
            },
          }
        ])

  What I'd really LIKE to do is this:

          navBarBase.value.reskin([
          { key:"logoSrc", value:pathname("@images/ics-logo-FINAL.png") },
          { key:"hamburgerButtonClasses", value:"ics-nav-hamburger-nav-button" },
          { key:"outerContainerClassesDesktop", value:"ics-navbar-boxshadow" },
          { key:"outerContainerClassesMobile", value:"ics-navbar-boxshadow" },
          { key:"button.foreground", value:"#555555"},
          { key:"button.iconDimension", value:{
              style: ButtonIconActiveHeight,
              active: '20px'
            },
          } 
        ])

  In order to make that work, there'd have to be some kind of a layer where setProp
  looks at your path "my.theme.heres.button.foreground" and then it determines that 
  you're setting a property 'foreground' on your button subtheme because it knows that 
  there's a subtheme sitting at "my.theme.heres.button". That's for another day.
  


  2. You should probably not do this in an animation. It's for atomic events like a scroll-top
  or a button press where the user wants to switch to night theme or something.

  
*/

export const MERGE_CLASS = 'class'
export const MERGE_STYLE = 'style'
export const STYLE_STRING = 'string'
export const THEME_TYPE = 'theme'
const mergeOrTypeOptionTypes = { 'class':true, 'style':true, 'theme':true, 'string':true }


/**
 * @param { 
 * 
 *   props,           Required. The props from the component. "theme" is required. Other props may override the theme. 
 *   
 *   defaultConfig,   Required. The default Config (kit.utils.Config) object  
 *   
 *   postSkin,        Optional. Callback for after a reskin has happened (e.g. refreshing event listeners)
 *   
 *   context,         Optional. The context argument from the setup function (if present, will expose the reskin function)
 *   
 *   expose,          Optional. Object. If present along with expose, then this object will be merged into
 *                              the object for the expose function.
 * 
 *   exposeOuter      Optional. Boolean. If true, then this will return the exposure object and it will be the 
 *                              component's responsibility to use it with the context.expose function. What's the 
 *                              the point of this? Understand that the setup function is not a class definition, and as such
 *                              there's problems with funcitons calling each other before they're defined. So in some instances 
 *                              you're going to find it enormously inconvenient to rearrange your code so that you can pass in 
 *                              some function to the expose object that you pass in here. In such cases, you can pass "true"
 *                              to exposeOuter, and an object will be passed to you, also called exposeOuter, and that will have 
 *                              all the functions from the themer built in, and additionally you can then merge your own special 
 *                              functions into that object and then pass it to the context's expose funciton yourself.
 * }
 * 
 * @returns { 
 *   getProp, 
 *   setProp,
 *   reskin,
 *   rpropHooks
 * }
 * 
 * @notes
 * 
 * @rprops
 * The special property "rprops" can be passed into the props as an array of strings. If this happens, then this will 
 * make those properies reactive. So if you pass in this array to rprops;
 * 
 * ['src', 'color']
 * 
 * Then any computed properties that use getProp('src') or getProp('color') will be reactively triggered if the component
 * receives new "src" or "color" properties from outside:
 * 
 * <MyCoolComponent src="someImageSrcService" color="myCoolColor"/>
 * 
 * @rpropHooks 
 * This is for cases where props have "rprops" specified. This means that in addition to the reskinning, some of the components 
 * props are reactive. In such cases, you might have some other code that needs to fire before a reactive property gets reset, 
 * e.g., scroll-fx re-registration. This object will be passed from this function and then you can bind callbacks to it using
 * the property name, e.g.
 * 
 * rpropsHooks['src'] = () => {
 *   scrollerReset()
 * }
 * 
 *
 */
export const themer = ({ props, defaultConfig, postSkin, context, expose, exposeOuter }) => {

  //we have an interactive id that we make here. The deal is that we make this 
  //available to the consuming component so that it can assign it as the id of the
  //main wrapper. We then expose a function getInteractiveID() and that will 
  //give use a way to use the getInteractiveID() with aria-controls elsewhere.
  //For instance, if this is modal with the ref myModal, then we can 
  //do this on the button:
  //<button :aria-controls="myModal.getInteractiveID()">Open Modal</button>
  const interactiveID = getRandomID()

  let propsOrDefaultTheme = new Config(props.theme || defaultConfig)

  let themeConfig = propsOrDefaultTheme

  let propsConfig = new Config(props)

  //the reactive ref for changes to this theme
  //This is so that changes made with the getProp function will propagate
  //through the reactivity and trigger a refresh
  const localReskin = ref(1)

  //Some of the properties we want to be reactive. For instance an image component has a property
  //"src" and we really want it to be reactive so that it will change when we assign a new src to it.
  //Properties are not reactive by default, and have to be made reactive through toRef.
  //
  //Understand this, that a lot of the machinery here is to make the theme *reskinnable* via the reskin and setProp 
  //functions but what we're doing here is making sure that a particular property is not just skinnable, but *reactive*
  //as well, via resetting a prop in an ordinary way, i.e. <ImgV2 src="getTheImage"/> where getTheImage is a reactive 
  //property
  const { rprops } = props
  const rpropHooks = {}
  if(rprops) {
    for(let i=0; i<rprops.length; i++) {
      const rprop = rprops[i]

      const r_ = toRef(props, rprop)

      watch(r_, (newVal, oldVal) => {
        if(newVal != oldVal) {
          const rpropHook = rpropHooks ? rpropHooks[rprop] || null : null
          
          if(rpropHook) {
            rpropHook()
          }

          //If the THEME itself is changing, then we're going to reset the
          //whole theme configuration instead of just a property
          if(rprop == 'theme') {
            themeConfig.resetConfig(newVal)
            localReskin.value++

          } else {
            setProp(rprop, newVal, true)
          }
        }
      })

    }
  }


  const _objToString = (obj) => {
    if(typeof obj == 'string') {
      return obj
    }
    if(!obj) {
      return ''
    }
    let str = ''
    Object.keys(obj).forEach((key) => { str += key+':'+obj[key]+';' }) 
    return str;
  }

  /**
   * 
   * @param {String.} property. Required. The name of the property. Can be dot-delimited to point to a deep property in the theme
   * @param {String. MERGE_CLASS, MERGE_STYLE or THEME_TYPE} mergeOrTypeOption. Optional
   * 
   * 
   * @returns The property value, respecting the merge option or the chain of precedence
   * i.e. props, then theme, then default options.
   * 
   * If mergeOrTypeOption is left out, then this will behave respecting the chain of precendence,
   * first attempting to retrieve the value from the props, then from the theme, then from the 
   * default options.
   * 
   * If mergeOrTypeOption is MERGE_CLASS or MERGE_STYLE, then this will assume that the property points to a string
   * in the theme/props and in that case it will merge the property from the props 
   * behind the property from the theme in a single string. In addition, if merge is
   * "style" then this will make sure that the two different merged strings are separated
   * with a semicolon ";" in keeping with the syntax of css style strings.
   * 
   * If mergeOrTypeOption is THEME_TYPE, then this will retrieve the subtheme, respecting
   * the chain of precedence, and convert to a theme wrapper if needs be.
   * 
   * Note, if the setProp was called with the 'force' flag, then this will return that value.
   * 
   */
  const getProp = (property, mergeOrTypeOption, debugCode) => {

    if(mergeOrTypeOption && !mergeOrTypeOptionTypes[mergeOrTypeOption.trim()]) {
      throw new Error("Unknown option: "+mergeOrTypeOption+" "+JSON.stringify(mergeOrTypeOptionTypes))
    }

    //this line makes it reactive, because we're consuming these *Reskin
    //reactive refs. Vue keeps track of these, and computed properties that consume them
    if(localReskin.value) {

      const fromTheme = themeConfig.getSetting(property)

      //If we're supposed to merge the style or class, then merge
      if(mergeOrTypeOption == MERGE_STYLE || mergeOrTypeOption == MERGE_CLASS) {

        let mergeStart = _objToString( fromTheme || defaultConfig.getSetting(property))
        const mergeEnd = _objToString( propsConfig.getSetting(property) || "")

        if(mergeOrTypeOption == MERGE_STYLE) {
          mergeStart = mergeStart ? endChar(mergeStart, ";") : ""
        } 

        return `${mergeStart}${mergeEnd}`.trim()
      }

      //We do this so that falsy values from the theme can override truthy default values.
      const r1 = propsConfig.getSetting(property)
      const r2 = defaultConfig.getSetting(property)

      if(r1 !== undefined && r1 !== null) {
        return r1
      } else 
      if(fromTheme !== undefined && fromTheme !== null) {
        return fromTheme
      } else 
      if(r2 !== undefined && r2 !== null) {
        return r2
      } else {
        return null
      }


    }
  }

  //Set a property, used for re-theming. 
  //If it's a subtheme, then you have to pass true to isTheme.
  /**
   * 
   * @param {String} property. Required. The name of the property. Can be dot-delimited to point to a deep property in the theme 
   * @param {Mixed} newVal. Required. The value to set the property to. 
   * @param {*} force. Optional. Default is false. Do we want to force an update?
   *    So here's the deal with the force flag. There's a chain of precedence. First props, then theme, then
   *    defaults. If you leave out force, then it will set it on theme, which is just fine, but if the 
   *    props override it, then it will have no effect. The getProp function will always defer to the props.
   *    So using the 'force' option will just set it on the props, effectively making it that highest priority.
   */
  const setProp = (property, newVal, force) => {

    if(newVal && newVal._isWrapper) {
      throw new Error("Error: setProp isn't meant to be used this way. You don't pass in ThemeWrapper objects")
    }

    //Select the proper configuration object on which to set the property and value.
    //If "force", is true, then it's going to set it on the props configuration, which 
    //will have the effect "forcing" a refresh, because the propsConfig is the highest
    //priority in the chain of precedence, meaning that it will be the property that 
    //gets read out by getProp no matter what. This is handy so that we can update
    //ui objects on the fly.
    const configObj = force ? propsConfig : themeConfig

    configObj.resetSetting(property, newVal )
    localReskin.value++
    
  }


  /**
   * This will apply a batch of alterations to the theme, and
   * call an optional callback afterwards, which is handy for refreshing event listeners. 
   * 
   * @param {Array<{key, value, type, force}>} skinItems. Required.
   *     An array of items to pass to the setProp.
   *  
   */
  const reskin = async(skinItems, force) => {    
    for(var i=0; i<skinItems.length; i++) {
      const { key, value } = skinItems[i] 
      setProp(key, value, force)
    }

    if(postSkin) {
      await nextTick()
      postSkin(getProp, setProp)
    }
  }

  let exposeOuterFromTheme = null

  //if context was passed in, then expose the reskin function.
  if(context || exposeOuter) {
    const ex = expose || {}

    //if this is modal with the ref myModal, then we can 
    //do this on the button:
    //<button :aria-controls="myModal.getInteractiveID()">Open Modal</button>
    const getInteractiveID = () => {
      return interactiveID
    }

    //Either call expose automatically 
    if(!exposeOuter) {
      context.expose({reskin, setProp, getInteractiveID, ...ex})
    } else 
    if(context) {
      exposeOuterFromTheme = { reskin, setProp, getInteractiveID }
    }
  }

 
  return { getProp, setProp, reskin, interactiveID, rpropHooks, exposeOuter:exposeOuterFromTheme }

}


/**
 * The factory for provide
 * 
 */
export const themerFactory = () => {
  const themerMethod = (obj1, obj2) => {
    const themerOutput = themer(obj1, obj2)
    return themerOutput
  }
  return themerMethod
}


